import React, { useMemo, useState } from 'react';
import { useGetAgentInboxSettingsQuery, useGetInboxListQuery, useUpdateAgentInboxSettingsMutation } from '@/store/emailInboxApi';
import { AgentInboxSettings } from '@/types/EmailInbox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { HtmlSignatureEditor } from './HtmlSignatureEditor';

export function AgentsTab() {
  const { data: agents = [], isLoading } = useGetAgentInboxSettingsQuery();
  const [selected, setSelected] = useState<AgentInboxSettings | null>(null);

  if (isLoading) return <div className="flex justify-center py-12"><Spinner /></div>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">{agents.length} נציגים</p>

      {agents.length === 0 ? (
        <div className="text-center py-16 text-gray-400 border border-dashed rounded-lg">
          <p>אין נציגים להצגה</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
              <tr>
                <th className="px-4 py-3 text-right">נציג</th>
                <th className="px-4 py-3 text-right">שם תצוגה</th>
                <th className="px-4 py-3 text-right">מייל</th>
                <th className="px-4 py-3 text-right">תיבת תגובה ברירת מחדל</th>
                <th className="px-4 py-3 text-right">פעולות</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {agents.map((agent) => (
                <tr key={agent.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">{agent.name || '—'}</td>
                  <td className="px-4 py-3">{agent.publish_name || '—'}</td>
                  <td className="px-4 py-3">{agent.email || '—'}</td>
                  <td className="px-4 py-3">{agent.reply_inbox_email || '—'}</td>
                  <td className="px-4 py-3">
                    <Button size="sm" variant="outline" onClick={() => setSelected(agent)}>
                      <Pencil className="h-4 w-4 ml-1" />
                      עריכה
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <AgentEditModal
          agent={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

function AgentEditModal({ agent, onClose }: { agent: AgentInboxSettings; onClose: () => void }) {
  const { data: inboxes = [] } = useGetInboxListQuery();
  const [updateAgent, { isLoading }] = useUpdateAgentInboxSettingsMutation();

  const [publishName, setPublishName] = useState(agent.publish_name || '');
  const [replyInbox, setReplyInbox] = useState<string>(agent.reply_inbox ? String(agent.reply_inbox) : 'none');
  const [signatureHtml, setSignatureHtml] = useState(agent.signature_html || '');

  const sendCapableInboxes = useMemo(
    () => inboxes.filter((i) => i.direction !== 'receive_only' && i.is_active),
    [inboxes],
  );

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    try {
      await updateAgent({
        id: agent.id,
        publish_name: publishName || null,
        reply_inbox: replyInbox === 'none' ? null : Number(replyInbox),
        signature_html: signatureHtml,
      }).unwrap();
      toast.success('הגדרות הנציג נשמרו');
      onClose();
    } catch (err: unknown) {
      const msg = (err as { data?: { detail?: string } })?.data?.detail ?? 'שגיאה בשמירה';
      toast.error(msg);
    }
  }

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl p-6">
        <DialogHeader>
          <DialogTitle>עריכת נציג: {agent.name || `#${agent.id}`}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSave} className="space-y-4">
          <div className="space-y-1">
            <Label>שם תצוגה (From Name)</Label>
            <Input
              value={publishName}
              onChange={(e) => setPublishName(e.target.value)}
              placeholder="שם שיופיע כשולח"
            />
          </div>

          <div className="space-y-1">
            <Label>תיבת תגובות ברירת מחדל</Label>
            <Select value={replyInbox} onValueChange={setReplyInbox}>
              <SelectTrigger>
                <SelectValue placeholder="ללא ברירת מחדל" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">ללא ברירת מחדל</SelectItem>
                {sendCapableInboxes.map((inbox) => (
                  <SelectItem key={inbox.id} value={String(inbox.id)}>
                    {inbox.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <HtmlSignatureEditor value={signatureHtml} onChange={setSignatureHtml} />

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>ביטול</Button>
            <Button type="submit" disabled={isLoading}>{isLoading ? 'שומר...' : 'שמור'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
